import { useState, useEffect } from "react";
import { Table, Button, Row, Col, Popconfirm, message, Tag, DatePicker, Select, Space } from "antd";
import { PlusOutlined, SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import Page from "component/Page/index";
import AddEditModal from "./AddEditModal";
import useAuthor from "hooks/useAuthor";
import AsyncButton from "component/AsyncButton";
import { useScheduleSurchargeManagerActions } from "@iso/actions/scheduleSurchargeManager.actions";
import * as moment from "moment";
import { COUNTRY_NAME } from "@iso/config/index";
import { useStoreManagerActions } from "@iso/actions/storeManager.actions";
import { COUNTRY_OPTIONS } from "@iso/constants";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ScheduleSurchargeManager = () => {
   const [isLoading, setIsLoading] = useState(true);
   const [isSubmitting, setIsSubmitting] = useState({});
   const [modalSurchargeState, setModalSurchargeState] = useState({
      visible: false,
      data: null,
   });
   const [data, setData] = useState({
      dataSource: [],
      paging: {
         current: 1,
         total: 0,
         pageSize: 10,
      },
   });
   const [filters, setFilters] = useState({
      dateRange: null,
      country: null,
   });

   const { checkRoles } = useAuthor();
   const surchargeActions = useScheduleSurchargeManagerActions();
   const storeManagerActions = useStoreManagerActions();

   const columns = [
      {
         title: "ID",
         dataIndex: "id",
         key: "id",
      },
      {
         title: "From Date",
         dataIndex: "date_from",
         key: "date_from",
         render: (record) => <span>{moment(record).format("DD/MM/YYYY")}</span>,
      },
      {
         title: "To Date",
         dataIndex: "date_to",
         key: "date_to",
         render: (record) => <span>{moment(record).format("DD/MM/YYYY")}</span>,
      },
      {
         title: "Country",
         dataIndex: "country",
         key: "country",
         render: (record) => <span>{COUNTRY_NAME[record]}</span>,
      },
      {
         title: "Surcharge Percent",
         dataIndex: "surcharge_percent",
         key: "surcharge_percent",
         render: (record) => (
            <Tag color="green">
               <span className="uppercase font-bold text-sl">{record} %</span>
            </Tag>
         ),
      },
      {
         title: "Content",
         dataIndex: "content",
         key: "content",
      },
      {
         title: "Description EN",
         dataIndex: "description_en",
         key: "description_en",
      },
      {
         title: "Description VI",
         dataIndex: "description_vi",
         key: "description_vi",
      },

      {
         title: "Actions",
         align: "center",
         fixed: "right",
         render: (record) => (
            <>
               <div className="flex justify-center gap-3">
                  {checkRoles(["schedule_surcharge_edit"]) && (
                     <Button
                        type="primary"
                        onClick={() =>
                           handleToggleModalSurchargeState(true, record)
                        }
                     >
                        Edit
                     </Button>
                  )}
                  {checkRoles(["schedule_surcharge_edit"]) && (
                     <Popconfirm
                        title="Are you sure to delete?"
                        onConfirm={() => handleDelete(record["id"])}
                        placement="bottom"
                        okText="Yes"
                        cancelText="No"
                     >
                        <AsyncButton
                           isLoading={isSubmitting[record["id"]] || false}
                           type="primary"
                           style={{ marginLeft: 8 }}
                        >
                           Delete
                        </AsyncButton>
                     </Popconfirm>
                  )}
               </div>
            </>
         ),
      },
   ];

   const getData = async (page = 1, limit = 10, filterParams = {}) => {
      try {
         setIsLoading(true);

         const params = {
            page: page,
            limit,
            ...filterParams,
         };

         const [fromDate, toDate] = filterParams?.dateRange || [];
         if (fromDate && toDate) {
            params.date_from = fromDate.format('YYYY-MM-DD');
            params.date_to = toDate.format('YYYY-MM-DD');
         }

         const resGet = await surchargeActions.getList(params);
         setData({
            paging: {
               current: parseInt(resGet.page),
               total: parseInt(resGet.total),
               pageSize: parseInt(resGet.limit),
            },
            dataSource: resGet?.data,
         });
      } finally {
         setIsLoading(false);
      }
   };

   const handleToggleModalSurchargeState = (visible, data) => {
      setModalSurchargeState({
         visible: visible,
         data: data || null,
      });
   };

   const handleDelete = async (id) => {
      try {
         const currentIsSubmitting = { ...isSubmitting };
         currentIsSubmitting[`${id}`] = true;
         setIsSubmitting(currentIsSubmitting);
         await surchargeActions.deleteById(id);
         message.success("Delete success!!!");
         handleSearch();
      } finally {
         const currentIsSubmitting = { ...isSubmitting };
         delete currentIsSubmitting[id];
         setIsSubmitting(currentIsSubmitting);
      }
   };

   const handleTableChange = (pagination) => {
      getData(pagination.current, pagination.pageSize, filters);
   };

   const handleDateRangeChange = (dates) => {
      setFilters(prev => ({
         ...prev,
         dateRange: dates
      }));
   };

   const handleCountryChange = (value) => {
      setFilters(prev => ({
         ...prev,
         country: value
      }));
   };

   const handleSearch = () => {
      getData(1, data.paging.pageSize, filters);
   };

   const handleReset = () => {
      setFilters({
         dateRange: null,
         country: null,
      });
      getData(1, data.paging.pageSize, {});
   };

   useEffect(() => {
      getData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   return (
      <Page title="Schedule Surcharge Manager">
         <div className="staff-setting-wrapper">
            {/* Filter Section */}
            <Row className="pb-4" gutter={[16, 16]}>
               <Col span={24}>
                  <div className="bg-gray-50 p-4 rounded-lg">
                     <Row gutter={[16, 16]} align="middle">
                        <Col xs={24} sm={12} md={8}>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                 Date Range
                              </label>
                              <RangePicker
                                 style={{ width: '100%' }}
                                 value={filters.dateRange}
                                 onChange={handleDateRangeChange}
                                 format="DD/MM/YYYY"
                                 placeholder={['From Date', 'To Date']}
                              />
                           </div>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                 Country
                              </label>
                              <Select
                                 style={{ width: '100%' }}
                                 placeholder="Select Country"
                                 value={filters.country}
                                 onChange={handleCountryChange}
                                 allowClear
                                 showSearch
                                 optionFilterProp="children"
                                 filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                 }
                              >
                                 {COUNTRY_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                       {option.label}
                                    </Option>
                                 ))}
                              </Select>
                           </div>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                           <div className="flex flex-col">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                 Actions
                              </label>
                              <Space>
                                 <Button
                                    type="primary"
                                    style={{ display: 'flex' }}
                                    className="items-center"
                                    icon={<SearchOutlined />}
                                    onClick={handleSearch}
                                 >
                                    Search
                                 </Button>
                                 <Button
                                    style={{ display: 'flex' }}
                                    className="items-center"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                 >
                                    Reset
                                 </Button>
                              </Space>
                           </div>
                        </Col>
                     </Row>
                  </div>
               </Col>
            </Row>

            {/* Add Button Section */}
            <Row className="pb-8">
               <Col span={12} />
               <Col span={3} offset={9} className="text-right">
                  {checkRoles(["schedule_surcharge_add"]) && (
                     <Button
                        type="primary"
                        onClick={() => handleToggleModalSurchargeState(true)}
                     >
                        <p className="flex items-center">
                           <PlusOutlined />
                           <span className="ml-2">Add</span>
                        </p>
                     </Button>
                  )}
               </Col>
            </Row>

            <AddEditModal
               modalLangState={modalSurchargeState}
               handleToggleModalSurchargeState={handleToggleModalSurchargeState}
               getData={() => handleSearch()}
            />

            <Table
               columns={columns?.filter((col) => {
                  if (col.title === "Actions") {
                     return checkRoles(["schedule_surcharge_edit"]);
                  }
                  return true;
               })}
               rowKey="key"
               dataSource={data.dataSource}
               loading={isLoading}
               pagination={{
                  pageSize: data?.paging?.pageSize,
                  total: data?.paging?.total,
                  current: data?.paging?.current,
               }}
               onChange={handleTableChange}
            />
         </div>
      </Page>
   );
};

export default ScheduleSurchargeManager;